import { TimeOptions } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { T } from "services/lang/LanguageService";
import { formatDisplayDatetime } from "utils/time-utils";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { ZonedDateTime, ZoneId } from "@js-joda/core";
import { WeatherComponentDatasetProps, WeatherMonitoringProps } from "../charts/interfaces";
import { DATE_FORMATTER_SECONDS } from "utils/constants";

const intervals = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

export enum TabTypes {
  TABS = "tabs",
  SUBTABS = "subTabs",
}

export interface WeatherTableProps {
  datasetsData: DatasetDataInfoResponse[];
  props: WeatherMonitoringProps;
  dayOffset: number;
  endHour: number;
  tabHeader: string;
}

export const generateTableData = (
  datasetsData: DatasetDataInfoResponse[],
  props: WeatherMonitoringProps,
  dayOffset: number,
  endHour: number = 23
) => {
  const now = new Date();
  now.setDate(now.getDate() + dayOffset);
  const data = [];
  const tableStartTime = new Date(now.setHours(0, 0, 0, 0));
  const tableEndTime = new Date(now.setHours(endHour, 0, 0, 0));

  const isSameTime = (time1: Date, time2: Date) => {
    return time1.getTime() === time2.getTime();
  };

  for (let time = tableStartTime; time <= tableEndTime; time.setHours(time.getHours() + 1)) {
    const rowData = {
      time: new Date(time),
      temperature: `${
        datasetsData
          ?.find(dataset => dataset.identifier === props.temperature?.dataset.identifier)
          ?.data.find(currentData => isSameTime(new Date(currentData.key), time))
          ?.value.toFixed(0) ?? "-"
      } °C`,
      windSpeed: `${
        datasetsData
          ?.find(dataset => dataset.identifier === props.windSpeed?.dataset.identifier)
          ?.data.find(currentData => isSameTime(new Date(currentData.key), time))
          ?.value.toFixed(0) ?? "-"
      } m/s`,
      windDirection: `${
        datasetsData
          ?.find(dataset => dataset.identifier === props.windDirection?.dataset.identifier)
          ?.data.find(currentData => isSameTime(new Date(currentData.key), time))
          ?.value.toFixed(0) ?? "-"
      } °`,
      gusture: `${
        datasetsData
          ?.find(dataset => dataset.identifier === props.gusture?.dataset.identifier)
          ?.data.find(currentData => isSameTime(new Date(currentData.key), time))
          ?.value.toFixed(0) ?? "-"
      } m/s`,
      precipitation: `${
        datasetsData
          ?.find(dataset => dataset.identifier === props.precipitation?.dataset.identifier)
          ?.data.find(currentData => isSameTime(new Date(currentData.key), time))
          ?.value.toFixed(0) ?? "-"
      } mm/h`,
      feelsLikeTemperature: `${
        datasetsData
          ?.find(dataset => dataset.identifier === props.feelsLikeTemperature?.dataset.identifier)
          ?.data.find(currentData => isSameTime(new Date(currentData.key), time))
          ?.value.toFixed(0) ?? "-"
      } °C`,
      humidity: `${
        datasetsData
          ?.find(dataset => dataset.identifier === props.humidity?.dataset.identifier)
          ?.data.find(currentData => isSameTime(new Date(currentData.key), time))
          ?.value.toFixed(0) ?? "-"
      } %`,
    };
    data.push(rowData);
  }

  return data;
};

export const getVisibleDatasets = (...datasets: WeatherComponentDatasetProps[]) => {
  return datasets
    .filter((dataset): dataset is WeatherComponentDatasetProps => dataset?.isVisible ?? false)
    .map(dataset => dataset!.dataset);
};

export const getStartEndTime = (timeInterval: string, startOrEnd: "start" | "end") => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();
  if (timeInterval === TimeOptions.HOURS.name) {
    if (startOrEnd === "start") {
      const previousInterval = intervals.reduce((prev, curr) => {
        return currentHour >= curr ? curr : prev;
      });

      const adjustedStartTime = new Date();
      if (currentHour === previousInterval && currentMinutes < 1) {
        adjustedStartTime.setHours(previousInterval - 6, 0, 0, 0);
      } else {
        adjustedStartTime.setHours(previousInterval, 0, 0, 0);
      }
      return adjustedStartTime;
    }
    const futureTime = new Date();
    futureTime.setHours(now.getHours() + 48);
    return futureTime;
  }
  if (startOrEnd === "start") {
    return new Date(now.setHours(0, 0, 0, 0));
  }
  const futureTime = new Date();
  futureTime.setHours(0, 0, 0, 0);
  futureTime.setDate(now.getDate() + 7);
  return futureTime;
};

export const getAverageWindDirection = (dataArr: InstantDoubleValue[], start: ZonedDateTime, end: ZonedDateTime) => {
  if (!Array.isArray(dataArr)) {
    easasAlert({
      summary: T("alert.error"),
      detail: T("alert.weather-monitoring.wind-direction-dataset-is-not-an-array"),
      severity: "error",
    });
    return 0;
  }

  const filteredData = dataArr.filter(data => {
    const localDate = ZonedDateTime.parse(data.key).toLocalDate();

    return localDate.compareTo(start.toLocalDate()) >= 0 && localDate.compareTo(end.toLocalDate()) <= 0;
  });

  const sinSum = filteredData.reduce((acc, data) => acc + Math.sin(data.value * (Math.PI / 180)), 0);
  const cosSum = filteredData.reduce((acc, data) => acc + Math.cos(data.value * (Math.PI / 180)), 0);

  const avgDirection = Math.atan2(sinSum, cosSum) * (180 / Math.PI);
  return (avgDirection + 360) % 360;
};
export const getMinMaxWindSpeed = (dataArr: InstantDoubleValue[], start: ZonedDateTime, end: ZonedDateTime) => {
  if (!Array.isArray(dataArr)) {
    easasAlert({
      summary: T("alert.error"),
      detail: T("alert.weather-monitoring.wind-speed-dataset-is-not-an-array"),
      severity: "error",
    });
    return { min: "-", max: "-" };
  }

  const filteredData = dataArr.filter(data => {
    const localDate = ZonedDateTime.parse(data.key).toLocalDate();
    return localDate.compareTo(start.toLocalDate()) >= 0 && localDate.compareTo(end.toLocalDate()) < 0;
  });

  const minWindSpeed = Math.min(...filteredData.map(data => data.value));
  const maxWindSpeed = Math.max(...filteredData.map(data => data.value));

  return {
    min: minWindSpeed === Infinity ? "-" : minWindSpeed.toFixed(0),
    max: maxWindSpeed === -Infinity ? "-" : maxWindSpeed.toFixed(0),
  };
};

export const getMaxParameter = (dataArr: InstantDoubleValue[], start: ZonedDateTime, end: ZonedDateTime, parameter: string) => {
  if (!Array.isArray(dataArr)) {
    easasAlert({
      summary: T("alert.error"),
      detail: T("error.weather-monitoring.parameter-dataset-is-not-an-array", { parameter }),
      severity: "error",
    });
    return "-";
  }

  const filteredData = dataArr.filter(data => {
    const dateTime = ZonedDateTime.parse(data.key);
    return dateTime.compareTo(start) >= 0 && dateTime.compareTo(end) <= 0;
  });

  const maxParameter = Math.max(...filteredData.map(data => data.value));
  return maxParameter === -Infinity ? "-" : `${maxParameter.toFixed(0)}`;
};

export const getCurrentHourParameter = (dataArr: InstantDoubleValue[], currentHour: ZonedDateTime) => {
  if (!Array.isArray(dataArr)) {
    easasAlert({
      summary: T("alert.error"),
      detail: T("alert.weather-monitoring.dataset-is-not-an-array"),
      severity: "error",
    });
    return "-";
  }

  const filteredData = dataArr.filter(data => {
    const dateTime = ZonedDateTime.parse(data.key);

    return (
      dateTime.year() === currentHour.year() &&
      dateTime.monthValue() === currentHour.monthValue() &&
      dateTime.dayOfMonth() === currentHour.dayOfMonth() &&
      dateTime.hour() === currentHour.hour()
    );
  });

  if (filteredData.length === 0) {
    return "-";
  }

  const currentHourValue = filteredData[0].value;

  return `${currentHourValue.toFixed(0)}`;
};

export const exportToCSV = (data: any[], fileName: string, zoneId: ZoneId) => {
  const csvRows = [];

  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(","));

  data.forEach(row => {
    const values = headers.map(header => {
      let value = row[header];
      if (value instanceof Date) {
        value = formatDisplayDatetime(value, zoneId, DATE_FORMATTER_SECONDS);
      }
      const escaped = `${value}`.replace(/"/g, '\\"');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(","));
  });

  const csvString = csvRows.join("\n");
  const blob = new Blob([csvString], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.setAttribute("hidden", "");
  a.setAttribute("href", url);
  a.setAttribute("download", `${fileName}.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
